import type { Ref } from 'vue';
import type {
	DropTarget,
	GridBlockPosition,
	GridLayoutData,
	OverlayCell,
	OverlayPreview,
	PreviewPosition,
	ResizeState,
} from './grid-types';
import { computed, watch } from 'vue';
import { GRID_CONFIG } from './grid-types';
import { calculateColumnGuideStyle, useGridBounds } from './use-grid-bounds';

export interface DragStateForOverlay {
	draggedChildId?: string;
}

export interface GridOverlayOptions {
	isDragging: Ref<boolean>;
	isResizing: Ref<boolean>;
	dragging: Ref<DragStateForOverlay | null>;
	resizing: Ref<ResizeState | null>;
	dragPreviewPosition: Ref<PreviewPosition | null>;
	resizePreviewPosition: Ref<GridBlockPosition | null>;
	dropTarget: Ref<DropTarget | null>;
	betweenRowHover: Ref<number | null>;
}

/**
 * Grid overlay composable
 * Provides unified state management for all grid overlay previews (drag & resize)
 */
export function useGridOverlay(
	gridData: Ref<GridLayoutData>,
	containerRef: Ref<HTMLElement | null>,
	rowRefs: Ref<Record<number, HTMLElement>>,
	options: GridOverlayOptions,
) {
	const {
		isDragging,
		isResizing,
		dragging,
		resizing,
		dragPreviewPosition,
		resizePreviewPosition,
		dropTarget,
		betweenRowHover,
	} = options;

	const isOverlayActive = computed(() => isDragging.value || isResizing.value);

	const { containerBounds, rowBounds, lockedRowHeights, updateBounds } = useGridBounds(
		gridData,
		containerRef,
		rowRefs,
		isOverlayActive,
	);

	watch([dragPreviewPosition, resizePreviewPosition], () => {
		if (isOverlayActive.value) {
			updateBounds();
		}
	});

	const activeRowIndex = computed<number | null>(() => {
		if (isResizing.value && resizing.value) {
			return resizing.value.rowIndex;
		}

		if (isDragging.value && dragPreviewPosition.value && !dragPreviewPosition.value.isNewRow) {
			return dragPreviewPosition.value.rowIndex;
		}

		return null;
	});

	const columnGuides = computed<OverlayCell[]>(() => {
		const rowIndex = activeRowIndex.value;

		if (rowIndex === null) return [];

		const row = gridData.value.rows[rowIndex];

		if (!row) return [];

		const guides: OverlayCell[] = [];
		const preview = isResizing.value ? resizePreviewPosition.value : dragPreviewPosition.value;

		for (let col = 0; col < row.columns; col++) {
			const isHighlighted = preview
				? col >= preview.colStart && col < preview.colStart + preview.colSpan
				: false;

			guides.push({
				rowIndex,
				colIndex: col,
				isHighlighted,
			});
		}

		return guides;
	});

	const previewBlock = computed<OverlayPreview | null>(() => {
		if (isResizing.value && resizePreviewPosition.value) {
			return {
				rowIndex: resizePreviewPosition.value.rowIndex,
				colStart: resizePreviewPosition.value.colStart,
				colSpan: resizePreviewPosition.value.colSpan,
				isNewRow: false,
				isValid: true,
			};
		}

		if (isDragging.value && dragPreviewPosition.value) {
			const preview = dragPreviewPosition.value;

			if (betweenRowHover.value !== null) {
				return {
					rowIndex: betweenRowHover.value,
					colStart: 0,
					colSpan: GRID_CONFIG.COLUMNS,
					isNewRow: true,
					isValid: true,
					insertBeforeRow: betweenRowHover.value,
				};
			}

			return {
				rowIndex: preview.rowIndex,
				colStart: preview.colStart,
				colSpan: preview.colSpan,
				isNewRow: preview.isNewRow,
				isValid: dropTarget.value?.valid ?? true,
				insertBeforeRow: preview.insertBeforeRow,
			};
		}

		return null;
	});

	const draggedBlockId = computed(() => {
		const d = dragging.value;

		if (!d) return null;

		return d.draggedChildId ?? null;
	});

	function getColumnGuideStyle(rowIndex: number, colIndex: number): Record<string, string> {
		return calculateColumnGuideStyle(rowIndex, colIndex, rowBounds.value, containerBounds.value);
	}

	return {
		isOverlayActive,
		activeRowIndex,
		columnGuides,
		previewBlock,
		draggedBlockId,
		containerBounds,
		rowBounds,
		lockedRowHeights,
		updateBounds,
		getColumnGuideStyle,
	};
}
